import moment, { Moment } from 'moment-timezone';
import { calculateTimeIntervals, formatTimeLabel, shouldShowTimeLabel } from './timespanConverter';

export type ChartTimeAxisConfig = {
  labels: string[];
  verticalLines: Array<Record<string, unknown>>;
  shouldShowLabel: (index: number) => boolean;
  formatLabel: (index: number) => string;
};

const snapTimestampToBucket = (
  timestamp: Moment,
  interval: number,
  showHalfHours: boolean,
): Moment => {
  if (showHalfHours) {
    const minute = timestamp.minute();
    return timestamp
      .clone()
      .minute(minute < 30 ? 0 : 30)
      .second(0)
      .millisecond(0);
  }

  const snappedHour = Math.floor(timestamp.hour() / interval) * interval;
  return timestamp
    .clone()
    .hour(snappedHour)
    .minute(0)
    .second(0)
    .millisecond(0);
};

export const buildChartTimeAxis = (
  timestampsIso: string[],
  startDateTime: Moment,
  endDateTime: Moment,
): ChartTimeAxisConfig => {
  const { interval, showHalfHours } = calculateTimeIntervals(startDateTime, endDateTime);
  const labels: string[] = [];
  const verticalLines: Array<Record<string, unknown>> = [];
  const snappedTimes: Moment[] = [];
  const labelIndices = new Set<number>();
  let previousDate = '';
  let lastBucketKey = '';
  let previousVisibleDate = '';

  timestampsIso.forEach((iso, index) => {
    const timestamp = moment.utc(iso).tz('America/New_York');
    labels.push(formatTimeLabel(timestamp, showHalfHours));
    const snapped = snapTimestampToBucket(timestamp, interval, showHalfHours);
    snappedTimes.push(snapped);

    // Only mark the first datapoint in each bucket (hour or half-hour) as a label anchor,
    // and only if that snapped bucket satisfies the env-graph rule for label visibility.
    const bucketKey = snapped.format('YYYY-MM-DD HH:mm');
    if (
      bucketKey !== lastBucketKey &&
      shouldShowTimeLabel(snapped, startDateTime, interval, showHalfHours)
    ) {
      labelIndices.add(index);
      lastBucketKey = bucketKey;
    }

    // Vertical date dashed line whenever the visible date changes
    const currentDate = timestamp.format('M/D');
    if (currentDate !== previousDate) {
      if (previousVisibleDate !== '') {
        verticalLines.push({
          xAxis: index,
          lineStyle: { color: 'lightgrey', type: 'dashed' },
          label: { formatter: currentDate, position: 'end', fontSize: 10, color: 'grey' },
          tooltip: { formatter: `Date: ${currentDate}` },
        });
      }
      previousVisibleDate = currentDate;
      previousDate = currentDate;
    }
  });

  return {
    labels,
    verticalLines,
    shouldShowLabel: (index: number) => labelIndices.has(index),
    formatLabel: (index: number) => {
      const snapped = snappedTimes[index];
      if (!snapped) return '';
      return formatTimeLabel(snapped, showHalfHours);
    },
  };
};

